const isLinkElement = (value: string) => {
  value = value.trim().toLowerCase();

  // ensure link is also a valid <a> element with an opening tag
  // that has to be followed by a whitespace because at least
  // the href attributes should exist (which we don't check here)
  return value.startsWith("<a ")
    && value.endsWith("</a>")
    && value.match(/<a\s/g).length === 1
    && value.match(/<\/a>/g).length === 1;
}

const isMinimalLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = wellformLink(value).trim().toLowerCase();

  return value.includes('href="');
}

const isQualifiedLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = wellformLink(value).trim().toLowerCase();

  return isMinimalLink(value) && value.includes('aria-label="');
}

const isTelLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = value.trim().toLowerCase();

  return value.includes('"tel:');
}

const isEmailLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = value.trim().toLowerCase();

  return value.includes('"mailto:');
}

const isNewTabLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = value.trim().toLowerCase();

  return value.includes('target="_blank"');
}

const isModalLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = value.trim().toLowerCase();

  return value.includes('showmodal=');
}

const hasLinks = (value: string) => {
  value = value.trim().toLowerCase();

  return value.includes('<a ');
}

const parseLinks = (value: string) => {
  const matches = value.matchAll(/<a\s[^>]*>[^<]+<\/a>/gi);
  return [...matches].flat();
}

const wellformLink = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  // ensure attribute values are double quoted
  return value.replaceAll("'", '"');
}

const getFormLabelExtension = (type: string, value: string) => {
  const formTypes = {
    'TEST_DRIVE': `Öffnet den Dialog zur Anfrage einer Probefahrt für den ${value}`,
    'REQUEST_FOR_PROPOSAL': 'Öffnet den Dialog zur Anfrage eines Angebots',
    'REQUEST_FOR_CONSULTATION': 'Öffnet den Dialog zur Anfrage eines Beratungstermins',
    'CAR_CONFIGURATOR': 'Öffnet per Klick auf der gleichen Seite oder kann über das Browserkontextmenü in einem neuen Fenster oder Tab geöffnet werden',
    'SERVICE_REQUEST': 'Öffnet das Modal zum Kontaktieren des Serviceberaters',
    'ONLINE_SERVICE_BOOKING': '?',
    'SERVICE_CONTACT': '?',
    'NEWSLETTER': `Öffnet den Dialog zur Newsletteranmeldung für den ${value}`,
    'USED_CAR_EVALUATION': 'Öffnet per Klick auf der gleichen Seite oder kann über das Browserkontextmenü in einem neuen Fenster oder Tab geöffnet werden',
  }

  if (!formTypes[type]) {
    throw new Error(`Form type ${value} does not exist`);
  }

  return formTypes[type];
}

const qualifyLinkForWcag = (value: string) => {
  if (!isLinkElement(value)) {
    throw new Error(`Value has to be link element only: ${value}`);
  }

  value = wellformLink(value);

  const [, label] = value.match(/>([^<]+)</i);
  const attributeMatches = value.matchAll(/\s([\w-]+)="([^"]+)"/gi);
  let attributes = [...attributeMatches.map((match) => {
    return {
      name: match[1],
      value: match[2],
    }
  })];
  const currentAriaLabel = attributes.find((attribute) => {
    return attribute.name === 'aria-label';
  });
  // remove aria-label if exists as it will be
  // attached after processing
  attributes = attributes.filter((attribute) => attribute.name !== 'aria-label');
  let ariaLabel = {
    name: 'aria-label',
    value: (currentAriaLabel ? currentAriaLabel.value : false) || label
  };

  if (isTelLink(value)) {
    const hrefValue = value.match(/tel:([^"]+)/i)[1];
    ariaLabel.value = createATcompatiblePhoneNumberText(hrefValue)
  }

  if (isEmailLink(value)) {
    const cleanedEmail = value.match(/mailto:([^"?]+)/i)[1];

    ariaLabel.value = `E-Mail an ${cleanedEmail} senden`;
  }

  if (isModalLink(value)) {
    const [ , formType ] = value.match(/modaltype=([^&w"]+)/i);
    const [ , carlineName ] = value.match(/carmodel=([^&"]+)/i);

    ariaLabel.value = getFormLabelExtension(formType, carlineName);
  }

  // as an extension of existing labels new tab links comes last
  if (isNewTabLink(value)) {
    ariaLabel.value += ` - öffnet in neuem Tab oder Fenster`;
  }

  attributes.push(ariaLabel);

  return `<a ${attributes.map((attribute) => `${attribute.name}="${attribute.value}"`).join(' ')}>${label}</a>`;
}

export const qualifyLinksForWcag = (value: string) => {
  if (!hasLinks(value)) {
    return value;
  }

  const links = parseLinks(value);
  const qualifiedLinks = links.map((link) => qualifyLinkForWcag(link));

  links.forEach((link, index) => {
    value = value.replaceAll(link, qualifiedLinks[index]);
  });

  return value;
}

// generate a phone number with three comma-separated numbers per block.
// With this technique, a phone number isn't read as a single number. Instead, the number is separated into blocks with speaking pauses.
const createATcompatiblePhoneNumberText = (value: string) => {
  let phoneNumber = value.replaceAll(' ', '').replace('tel:', '');
  let text = 'Telefonnummer';

  if (phoneNumber.startsWith('+49')) {
    text += ' mit Vorwahl aus Deutschland';
    phoneNumber = phoneNumber.replace('+49', '0');
  }

  text +=
    ' ' +
    phoneNumber
      .split('')
      .map((character, characterIndex) => {
        let result = character;
        const isLastIndex = characterIndex === phoneNumber.length - 1;
        if (!isLastIndex) {
          result += ' ';
        }
        if ((characterIndex + 1) % 3 === 0 && !isLastIndex) {
          result += ', ';
        }
        return result;
      })
      .join('');
  text += ' anrufen';

  return text;
}

export const LinkUtils = {
  isLinkElement,
  isMinimalLink,
  isQualifiedLink,
  isTelLink,
  isEmailLink,
  isNewTabLink,
  isModalLink,
  hasLinks,
  parseLinks,
  wellformLink,
  getFormLabelExtension,
  createATcompatiblePhoneNumberText,
  qualifyLinkForWcag,
}
